import { isEmpty, isString, uniq } from 'lodash'
import * as Yup from 'yup'

import { isValidPath, PATH_MESSAGE } from '../pathMapping/paths'
import { METADATA_KEY_PATTERN } from '../tcn/tokenSources'

/**
 * Field names, kept in one place so a backend rename is a single edit here.
 *
 * These match the column names in agent_connector_generic_configs exactly. The
 * backend serialises its config with a getattr allowlist keyed on the same names, so
 * they must stay in sync.
 */
export const GENERIC_FIELDS = {
  agentIdMethod: 'remote_id_method',
  agentIdPath: 'remote_id_source',
  eventTypePath: 'event_type_path',
  startValues: 'start_values',
  stopValues: 'stop_values',
  callIdPath: 'voip_call_id_path',
  customerIdPath: 'voip_customer_id_path',
  campaignNameFields: 'voip_campaign_name_fields',
  directionPath: 'direction_path',
  directionMap: 'direction_map',
  callMetadataFields: 'call_metadata_fields',
  dispositionPath: 'disposition_path',
  dispositionValues: 'disposition_values',
  dispositionCallIdPath: 'disposition_call_id_path',
  dispositionAgentIdPath: 'disposition_remote_id_source',
  vendor: 'vendor',
}

export const DIRECTION_OPTIONS = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
]

export const CAMPAIGN_PATHS_SCHEMA = Yup.array().of(
  Yup.string().test('valid-path', PATH_MESSAGE, isValidPath)
)

export const GENERIC_CALL_METADATA_SCHEMA = Yup.array().of(
  Yup.object({
    key: Yup.string()
      .required('Name is required')
      .matches(
        METADATA_KEY_PATTERN,
        'Letters, numbers and underscores only — no spaces or brackets'
      ),
    // "source" rather than "path", matching TcnCallMetadataField so both
    // connectors store the same row shape.
    source: Yup.string().test('valid-path', PATH_MESSAGE, isValidPath),
  })
)

const eventValuesSchema = (verb) =>
  Yup.array()
    .of(Yup.string().trim().required('Enter the value the vendor sends'))
    .min(1, `Add at least one value that means the call ${verb}`)

// Matched the way the backend matches: casefolded and trimmed, so "Answered" and
// "answered " are the same rule.
const matchKeys = (rows) =>
  new Set(
    (rows ?? [])
      .map((row) => (isString(row) ? row.trim().toLowerCase() : ''))
      .filter((value) => !isEmpty(value))
  )

// A value in two lists makes one event two things at once. The backend refuses it at
// save and again at activation, but only as a 422 toast or a NEEDS_REVIEW pill whose
// reason lives in the logs -- neither of which tells the admin which value or which
// field. Checking here puts the message on the field.
//
// Applied to stop (against start) and to disposition (against both), which covers
// all three pairs while showing the message once rather than on both sides.
const noOverlapWith =
  (...siblingFields) =>
  (rows, context) => {
    const mine = matchKeys(rows)
    if (isEmpty(mine)) {
      return true
    }
    return !siblingFields.some((field) =>
      [...matchKeys(context.parent?.[field])].some((value) => mine.has(value))
    )
  }

export const START_VALUES_SCHEMA = eventValuesSchema('started')

export const STOP_VALUES_SCHEMA = eventValuesSchema('stopped').test(
  'no-overlap',
  'A value here also appears under START or DISPOSITION. Each value may only appear in one list.',
  noOverlapWith(GENERIC_FIELDS.startValues, GENERIC_FIELDS.dispositionValues)
)

// No .min(1): unlike start and stop, a disposition-only event is a shape some
// vendors have and most do not, so an empty list is the normal case.
export const DISPOSITION_VALUES_SCHEMA = Yup.array()
  .of(Yup.string().trim().required('Enter the value the vendor sends'))
  .test(
    'no-overlap',
    'A value here also appears under START or STOP. Each value may only appear in one list.',
    noOverlapWith(GENERIC_FIELDS.startValues, GENERIC_FIELDS.stopValues)
  )

export const DIRECTION_MAP_SCHEMA = Yup.array()
  .of(
    Yup.object({
      from: Yup.string().trim().required('Enter the value the vendor sends'),
      to: Yup.string()
        .oneOf(['inbound', 'outbound'], 'Choose inbound or outbound')
        .required('Choose inbound or outbound'),
    })
  )
  // Array-level, so it renders once through ArrayLevelError rather than blaming a
  // single row. The backend rejects duplicates case-insensitively for the same
  // reason: the runtime lookup casefolds, so letting both save would make which one
  // wins depend on row order.
  .test('unique-from', 'Each vendor value can only be mapped once', (rows) => {
    const values = (rows ?? [])
      .map((row) => row?.from?.trim()?.toLowerCase())
      .filter((value) => !isEmpty(value))
    return uniq(values).length === values.length
  })
